<template>
  <div :class="themeClasses.pageContainer" class="usuarios-config">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h4 :class="themeClasses.headingText" class="mb-1">Gestión de Usuarios</h4>
        <p :class="themeClasses.mutedText" class="mb-0">Administra usuarios y permisos del sistema</p>
      </div>
      <button 
        :class="themeClasses.buttonPrimary" 
        class="btn btn-primary" 
        @click="abrirModalUsuario()"
      >
        <i class="fas fa-user-plus me-2"></i>
        Nuevo Usuario
      </button>
    </div>

    <!-- Filtros y Búsqueda -->
    <div :class="themeClasses.card" class="card mb-4">
      <div :class="themeClasses.cardBody" class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <div :class="themeClasses.inputGroup" class="input-group">
              <span :class="themeClasses.inputGroupText" class="input-group-text">
                <i class="fas fa-search"></i>
              </span>
              <input
                type="text"
                :class="themeClasses.formControl"
                class="form-control"
                placeholder="Buscar usuarios..."
                v-model="filtros.busqueda"
                @input="buscarUsuarios"
              >
            </div>
          </div>
          <div class="col-md-3">
            <select class="form-select" v-model="filtros.estado" @change="cargarUsuarios">
              <option value="">Todos los estados</option>
              <option value="activo">Activos</option>
              <option value="inactivo">Inactivos</option>
            </select>
          </div>
          <div class="col-md-3">
            <select class="form-select" v-model="filtros.rol" @change="cargarUsuarios">
              <option value="">Todos los roles</option>
              <option value="admin">Administrador</option>
              <option value="manager">Gerente</option>
              <option value="user">Usuario</option>
            </select>
          </div>
          <div class="col-md-2">
            <button 
              class="btn btn-outline-secondary w-100"
              @click="limpiarFiltros"
            >
              <i class="fas fa-times me-2"></i>
              Limpiar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando usuarios...</p>
    </div>

    <!-- Tabla de Usuarios -->
    <div v-else class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <i class="fas fa-users text-primary me-2"></i>
          Usuarios Registrados ({{ usuarios.length }})
        </h6>
        <div class="d-flex gap-2">
          <button 
            class="btn btn-outline-success btn-sm"
            @click="exportarUsuarios"
            :disabled="exportando"
          >
            <i v-if="exportando" class="fas fa-spinner fa-spin me-2"></i>
            <i v-else class="fas fa-download me-2"></i>
            Exportar
          </button>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Usuario</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Último Acceso</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="usuario in usuariosPaginados" :key="usuario.id">
              <td>
                <div class="d-flex align-items-center">
                  <div class="avatar-circle me-3">
                    {{ iniciales(usuario.full_name) }}
                  </div>
                  <div>
                    <div class="fw-bold">{{ usuario.full_name }}</div>
                    <small class="text-muted">{{ usuario.username }}</small>
                  </div>
                </div>
              </td>
              <td>{{ usuario.email }}</td>
              <td>
                <span 
                  class="badge"
                  :class="badgeRol(usuario.role)"
                >
                  {{ formatearRol(usuario.role) }}
                </span>
              </td>
              <td>
                <span 
                  class="badge"
                  :class="usuario.is_active ? 'bg-success' : 'bg-danger'"
                >
                  {{ usuario.is_active ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
              <td>
                <small class="text-muted">
                  {{ formatearFecha(usuario.last_login) }}
                </small>
              </td>
              <td>
                <div class="btn-group" role="group">
                  <button 
                    class="btn btn-outline-primary btn-sm"
                    @click="abrirModalUsuario(usuario)"
                    title="Editar usuario"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    class="btn btn-outline-info btn-sm"
                    @click="abrirModalPermisos(usuario)"
                    title="Gestionar permisos"
                  >
                    <i class="fas fa-key"></i>
                  </button>
                  <button 
                    class="btn btn-outline-warning btn-sm"
                    @click="toggleEstadoUsuario(usuario)"
                    :title="usuario.is_active ? 'Desactivar usuario' : 'Activar usuario'"
                  >
                    <i :class="usuario.is_active ? 'fas fa-user-slash' : 'fas fa-user-check'"></i>
                  </button>
                  <button 
                    class="btn btn-outline-danger btn-sm"
                    @click="confirmarEliminar(usuario)"
                    title="Eliminar usuario"
                    :disabled="usuario.role === 'admin' && usuariosAdmin === 1"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Sin datos -->
      <div v-if="usuarios.length === 0" class="text-center py-5">
        <i class="fas fa-users fa-3x text-muted mb-3"></i>
        <h5 class="text-muted">No hay usuarios registrados</h5>
        <p class="text-muted">Comienza creando el primer usuario del sistema</p>
        <button class="btn btn-primary" @click="abrirModalUsuario()">
          <i class="fas fa-user-plus me-2"></i>
          Crear Usuario
        </button>
      </div>

      <!-- Paginación -->
      <div v-if="usuarios.length > itemsPorPagina" class="card-footer">
        <nav>
          <ul class="pagination justify-content-center mb-0">
            <li class="page-item" :class="{ disabled: paginaActual === 1 }">
              <a class="page-link" href="#" @click.prevent="cambiarPagina(paginaActual - 1)">
                <i class="fas fa-chevron-left"></i>
              </a>
            </li>
            <li 
              v-for="pagina in totalPaginas" 
              :key="pagina"
              class="page-item"
              :class="{ active: pagina === paginaActual }"
            >
              <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina)">
                {{ pagina }}
              </a>
            </li>
            <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
              <a class="page-link" href="#" @click.prevent="cambiarPagina(paginaActual + 1)">
                <i class="fas fa-chevron-right"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Resumen de usuarios activos -->
    <div :class="themeClasses.card" class="card mt-4">
      <div :class="themeClasses.cardHeader" class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <i class="fas fa-user-check text-success me-2"></i>
          Usuarios activos por tipo
        </h6>
        <span class="badge bg-secondary">
          Total: {{ usuariosActivosResumen.totals.overall }}
        </span>
      </div>
      <div :class="themeClasses.cardBody" class="card-body">
        <div v-if="loadingUsuariosActivos" class="text-center text-muted py-4">
          <i class="fas fa-spinner fa-spin fa-2x mb-2"></i>
          <div>Cargando usuarios activos...</div>
        </div>
        <div v-else class="row g-4">
          <div class="col-md-6">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">
                <i class="fas fa-shield-alt text-danger me-2"></i>
                Administradores del sistema
              </h6>
              <span class="badge bg-danger">{{ usuariosActivosResumen.totals.admin }}</span>
            </div>
            <ul class="list-group list-group-flush">
              <li
                v-for="usuario in usuariosActivosResumen.admin"
                :key="'admin-activo-' + usuario.id"
                class="list-group-item px-0"
              >
                <div class="d-flex flex-column flex-md-row justify-content-between gap-2">
                  <div>
                    <div class="fw-semibold">{{ usuario.full_name }}</div>
                    <small class="text-muted">{{ usuario.email }}</small>
                  </div>
                  <div class="text-md-end">
                    <span class="badge me-2" :class="badgeRol(usuario.role)">
                      {{ formatearRol(usuario.role) }}
                    </span>
                    <div class="small text-muted mt-1">
                      {{ empresaRepresentada(usuario) }}
                    </div>
                  </div>
                </div>
              </li>
              <li
                v-if="usuariosActivosResumen.admin.length === 0"
                class="list-group-item text-muted fst-italic px-0"
              >
                No hay administradores activos registrados.
              </li>
            </ul>
          </div>
          <div class="col-md-6">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">
                <i class="fas fa-users text-primary me-2"></i>
                Usuarios cliente
              </h6>
              <span class="badge bg-primary">{{ usuariosActivosResumen.totals.client }}</span>
            </div>
            <ul class="list-group list-group-flush">
              <li
                v-for="usuario in usuariosActivosResumen.client"
                :key="'cliente-activo-' + usuario.id"
                class="list-group-item px-0"
              >
                <div class="d-flex flex-column flex-md-row justify-content-between gap-2">
                  <div>
                    <div class="fw-semibold">{{ usuario.full_name }}</div>
                    <small class="text-muted">{{ usuario.email }}</small>
                  </div>
                  <div class="text-md-end">
                    <span class="badge me-2" :class="badgeTipoUsuario(usuario.type)">
                      {{ formatearTipoUsuario(usuario.type) }}
                    </span>
                    <div class="small text-muted mt-1">
                      {{ empresaRepresentada(usuario) }}
                    </div>
                  </div>
                </div>
              </li>
              <li
                v-if="usuariosActivosResumen.client.length === 0"
                class="list-group-item text-muted fst-italic px-0"
              >
                No hay usuarios cliente activos registrados.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Usuario -->
    <div 
      class="modal fade" 
      id="modalUsuario" 
      tabindex="-1" 
      aria-hidden="true"
      ref="modalUsuario"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-user me-2"></i>
              {{ modoEdicion ? 'Editar Usuario' : 'Nuevo Usuario' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardarUsuario">
            <div class="modal-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Nombre Completo *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="usuarioForm.full_name"
                      :class="{ 'is-invalid': errores.full_name }"
                      required
                      maxlength="255"
                    >
                    <div v-if="errores.full_name" class="invalid-feedback">
                      {{ errores.full_name }}
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Nombre de Usuario *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="usuarioForm.username"
                      :class="{ 'is-invalid': errores.username }"
                      required
                      maxlength="50"
                      :readonly="modoEdicion"
                    >
                    <div v-if="errores.username" class="invalid-feedback">
                      {{ errores.username }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Email *</label>
                    <input
                      type="email"
                      class="form-control"
                      v-model="usuarioForm.email"
                      :class="{ 'is-invalid': errores.email }"
                      required
                    >
                    <div v-if="errores.email" class="invalid-feedback">
                      {{ errores.email }}
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Rol *</label>
                    <select 
                      class="form-select"
                      v-model="usuarioForm.role"
                      :class="{ 'is-invalid': errores.role }"
                      required
                    >
                      <option value="">Seleccionar rol...</option>
                      <option value="admin">Administrador</option>
                      <option value="manager">Gerente</option>
                      <option value="user">Usuario</option>
                    </select>
                    <div v-if="errores.role" class="invalid-feedback">
                      {{ errores.role }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="row" v-if="!modoEdicion">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Contraseña *</label>
                    <input
                      type="password"
                      class="form-control"
                      v-model="usuarioForm.password"
                      :class="{ 'is-invalid': errores.password }"
                      required
                      minlength="6"
                    >
                    <div v-if="errores.password" class="invalid-feedback">
                      {{ errores.password }}
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Confirmar Contraseña *</label>
                    <input
                      type="password"
                      class="form-control"
                      v-model="usuarioForm.password_confirmation"
                      :class="{ 'is-invalid': errores.password_confirmation }"
                      required
                    >
                    <div v-if="errores.password_confirmation" class="invalid-feedback">
                      {{ errores.password_confirmation }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="usuarioForm.is_active"
                    id="usuarioActivo"
                  >
                  <label class="form-check-label" for="usuarioActivo">
                    Usuario activo
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Cancelar
              </button>
              <button 
                type="submit" 
                class="btn btn-primary"
                :disabled="guardandoUsuario"
              >
                <i v-if="guardandoUsuario" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-save me-2"></i>
                {{ modoEdicion ? 'Actualizar' : 'Crear' }} Usuario
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal de Permisos -->
    <AdminConfiguracionPermisos
      v-if="usuarioPermisos"
      :usuario="usuarioPermisos"
      :visible="modalPermisosVisible"
      @close="cerrarModalPermisos"
      @saved="permisosActualizados"
    />
  </div>
</template>

<script>
import { Modal } from 'bootstrap'
import ConfiguracionService from '@/services/configuracion'
import { Toast } from '@/utils/notifications'
import AdminConfiguracionPermisos from './AdminConfiguracionPermisos.vue'

import themeMixin from '@/mixins/themeMixin'

export default {
  mixins: [themeMixin],
  name: 'AdminConfiguracionUsuarios',
  components: {
    AdminConfiguracionPermisos
  },
  data() {
    return {
      loading: true,
      guardandoUsuario: false,
      exportando: false,
      
      usuarios: [],
      usuariosActivosResumen: {
        admin: [],
        client: [],
        totals: {
          admin: 0,
          client: 0,
          overall: 0
        }
      },
      loadingUsuariosActivos: false,
      modoEdicion: false,
      modalUsuario: null,
      modalPermisosVisible: false,
      usuarioPermisos: null,
      
      // Filtros
      filtros: {
        busqueda: '',
        estado: '',
        rol: ''
      },
      
      // Paginación
      paginaActual: 1,
      itemsPorPagina: 10,
      
      // Formulario de usuario
      usuarioForm: {
        id: null,
        full_name: '',
        username: '',
        email: '',
        role: '',
        password: '',
        password_confirmation: '',
        is_active: true
      },
      
      errores: {},
      timeoutBusqueda: null
    }
  },
  computed: {
    usuariosPaginados() {
      const inicio = (this.paginaActual - 1) * this.itemsPorPagina
      const fin = inicio + this.itemsPorPagina
      return this.usuarios.slice(inicio, fin)
    },
    
    totalPaginas() {
      return Math.ceil(this.usuarios.length / this.itemsPorPagina)
    },
    
    usuariosAdmin() {
      return this.usuarios.filter(u => u.role === 'admin' && u.is_active).length
    }
  },
  async mounted() {
    await this.cargarUsuarios()
    await this.cargarUsuariosActivos()
    this.inicializarModal()
  },
  methods: {
    inicializarModal() {
      this.$nextTick(() => {
        if (this.$refs.modalUsuario) {
          this.modalUsuario = new Modal(this.$refs.modalUsuario)
        }
      })
    },

    async cargarUsuarios() {
      try {
        this.loading = true
        
        const params = {
          busqueda: this.filtros.busqueda,
          estado: this.filtros.estado,
          rol: this.filtros.rol
        }
        
        const response = await ConfiguracionService.getUsuarios(params)
        
        if (response.success) {
          this.usuarios = response.data.usuarios || []
        } else {
          Toast.error('Error al cargar usuarios')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al cargar usuarios')
      } finally {
        this.loading = false
      }
    },

    async cargarUsuariosActivos() {
      try {
        this.loadingUsuariosActivos = true

        const response = await ConfiguracionService.getUsuariosActivos()

        if (response.success) {
          const data = response.data || {}
          const totals = data.totals || {}

          this.usuariosActivosResumen = {
            admin: data.admin || [],
            client: data.client || [],
            totals: {
              admin: totals.admin ?? (data.admin ? data.admin.length : 0),
              client: totals.client ?? (data.client ? data.client.length : 0),
              overall:
                totals.overall ??
                ((data.admin ? data.admin.length : 0) + (data.client ? data.client.length : 0))
            }
          }
        } else {
          Toast.error('Error al cargar usuarios activos')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al cargar usuarios activos')
      } finally {
        this.loadingUsuariosActivos = false
      }
    },

    buscarUsuarios() {
      clearTimeout(this.timeoutBusqueda)
      this.timeoutBusqueda = setTimeout(() => {
        this.paginaActual = 1
        this.cargarUsuarios()
      }, 500)
    },

    limpiarFiltros() {
      this.filtros = {
        busqueda: '',
        estado: '',
        rol: ''
      }
      this.paginaActual = 1
      this.cargarUsuarios()
    },

    abrirModalUsuario(usuario = null) {
      this.modoEdicion = !!usuario
      this.errores = {}
      
      if (usuario) {
        this.usuarioForm = {
          id: usuario.id,
          full_name: usuario.full_name,
          username: usuario.username,
          email: usuario.email,
          role: usuario.role,
          password: '',
          password_confirmation: '',
          is_active: usuario.is_active
        }
      } else {
        this.usuarioForm = {
          id: null,
          full_name: '',
          username: '',
          email: '',
          role: '',
          password: '',
          password_confirmation: '',
          is_active: true
        }
      }
      
      this.modalUsuario.show()
    },

    async guardarUsuario() {
      try {
        this.guardandoUsuario = true
        this.errores = {}

        const response = this.modoEdicion 
          ? await ConfiguracionService.updateUsuario(this.usuarioForm.id, this.usuarioForm)
          : await ConfiguracionService.createUsuario(this.usuarioForm)
        
        if (response.success) {
          await this.cargarUsuarios()
          await this.cargarUsuariosActivos()
          this.modalUsuario.hide()
          
          Toast.success(
            this.modoEdicion 
              ? 'Usuario actualizado correctamente'
              : 'Usuario creado correctamente'
          )
        } else {
          if (response.details) {
            this.errores = response.details
          }
          Toast.error(response.error || 'Error al guardar usuario')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al guardar usuario')
      } finally {
        this.guardandoUsuario = false
      }
    },

    async toggleEstadoUsuario(usuario) {
      try {
        const nuevoEstado = !usuario.is_active
        const response = await ConfiguracionService.updateUsuario(usuario.id, {
          is_active: nuevoEstado
        })
        
        if (response.success) {
          usuario.is_active = nuevoEstado
          await this.cargarUsuariosActivos()
          Toast.success(
            nuevoEstado 
              ? 'Usuario activado correctamente'
              : 'Usuario desactivado correctamente'
          )
        } else {
          Toast.error('Error al cambiar estado del usuario')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al cambiar estado del usuario')
      }
    },

    async confirmarEliminar(usuario) {
      if (usuario.role === 'admin' && this.usuariosAdmin === 1) {
        Toast.error('No puedes eliminar el último administrador del sistema')
        return
      }

      const result = await window.Swal.fire({
        title: 'Eliminar Usuario',
        text: `¿Estás seguro de eliminar al usuario "${usuario.full_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      })

      if (result.isConfirmed) {
        this.eliminarUsuario(usuario)
      }
    },

    async eliminarUsuario(usuario) {
      try {
        const response = await ConfiguracionService.deleteUsuario(usuario.id)
        
        if (response.success) {
          await this.cargarUsuarios()
          await this.cargarUsuariosActivos()
          Toast.success('Usuario eliminado correctamente')
        } else {
          Toast.error('Error al eliminar usuario')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al eliminar usuario')
      }
    },

    abrirModalPermisos(usuario) {
      this.usuarioPermisos = usuario
      this.modalPermisosVisible = true
    },

    cerrarModalPermisos() {
      this.modalPermisosVisible = false
      this.usuarioPermisos = null
    },

    permisosActualizados() {
      Toast.success('Permisos actualizados correctamente')
      this.cerrarModalPermisos()
    },

    async exportarUsuarios() {
      try {
        this.exportando = true
        
        const response = await ConfiguracionService.exportarUsuarios()
        
        // Crear enlace de descarga
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `usuarios-${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        
        Toast.success('Usuarios exportados correctamente')
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al exportar usuarios')
      } finally {
        this.exportando = false
      }
    },

    cambiarPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginaActual = pagina
      }
    },

    iniciales(nombre) {
      return nombre
        .split(' ')
        .map(n => n.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },

    badgeRol(rol) {
      const badges = {
        admin: 'bg-danger',
        manager: 'bg-warning',
        user: 'bg-info'
      }
      return badges[rol] || 'bg-secondary'
    },

    formatearRol(rol) {
      const roles = {
        admin: 'Administrador',
        manager: 'Gerente',
        user: 'Usuario'
      }
      return roles[rol] || rol
    },

    formatearTipoUsuario(tipo) {
      const tipos = {
        admin: 'Administrador del sistema',
        client: 'Usuario cliente'
      }
      return tipos[tipo] || tipo
    },

    badgeTipoUsuario(tipo) {
      const badges = {
        admin: 'bg-danger',
        client: 'bg-primary'
      }
      return badges[tipo] || 'bg-secondary'
    },

    empresaRepresentada(usuario) {
      if (!usuario || !usuario.company) {
        return 'Sin empresa asociada'
      }

      const nombre = usuario.company.name || `Empresa #${usuario.company.id}`
      if (usuario.company.ruc) {
        return `${nombre} - RUC ${usuario.company.ruc}`
      }

      return nombre
    },

    formatearFecha(fecha) {
      if (!fecha) return 'Nunca'
      return new Date(fecha).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.usuarios-config {
  padding: 1.5rem;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.875rem;
}

.table th {
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.btn-group .btn {
  padding: 0.25rem 0.5rem;
}

.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
}

.modal-lg {
  max-width: 800px;
}

.pagination .page-link {
  color: #495057;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
}

@media (max-width: 768px) {
  .usuarios-config {
    padding: 1rem;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .btn-group .btn {
    padding: 0.2rem 0.4rem;
  }
}
</style>
